import React, { useCallback } from "react";
import { useHistory } from "react-router-dom";
import classes from "./style.module.scss";
import { Notification } from "~/types";
import LinkWrapper from "~/app/common/LinkWrapper";
import { MACHINES_DETAILS_ROUTES } from "~/machines/Details/Router";
import { route } from "~/app/helpers/route";
import { PROJECTS_ROUTES } from "~/projects/Router";
import moment from "moment";
import classNames from "classnames";
import UserAvatar from "~/user/UserAvatar";
import DOMPurify from 'dompurify';
import { MessageType } from "~/app/data/statuses/chat";
import { useNotificationContext } from "~/app/hooks/useNotificationContext";
import { REQUESTS_ROUTES } from "~/notifications/Router";
import { useStoreActions, useStoreState } from "~/store";
import useWebSocket from "~/app/hooks/useWebSocket";
import Icon from "~/app/common/Icon";
import { TIME_MANAGEMENT_ROUTES } from "~/timeManagement/Router";
import { TRAINING_ROUTES } from "~/training/Router";

export interface ListItemProps {
    item: Notification;
}

export type ListItemComponent = React.FC<ListItemProps>;

const ListItem: ListItemComponent = ({ item }) => {
    const history = useHistory();
    const { isChat } = useNotificationContext();
    const messageSentDateTime =
        moment(item.createdAt).isSame(moment(), "day")
            ? moment(item.createdAt).format("HH:mm")
            : moment(item.createdAt).format("DD.MM.YYYY");
    const unreadClass = !item.messageStatus ? classes["unread"] : "";
    const cleanHTML = DOMPurify.sanitize(item.message); // Santinize html before using
    const updateNotificationsStatusAction = useStoreActions((actions) => actions.notification.updateNotificationsStatus);
    const netWorkStatus = useStoreState(state => state.app.offline.status);
    const { updateUnreadCount } = useWebSocket();
    const importance = Number(item.importance);

    const getTargetRoute = useCallback((): string | { pathname: string; state: Record<string, unknown> } => {
        if (item.messageType === MessageType.SERVICE) {
            return route(MACHINES_DETAILS_ROUTES.serviceDetails, {
                id: item.machineId,
                serviceId: item.issueId,
            });
        }

        if (item.messageType === MessageType.TIME_MANAGEMENT) {
            return route(TIME_MANAGEMENT_ROUTES.details);
        }

        if (item.messageType === MessageType.TRAINING) {
            return route(TRAINING_ROUTES.details, {id: item.issueId });
        }

        if (item.messageType === MessageType.NOTIFICATION) {
            return REQUESTS_ROUTES.notifications;
        }

        if (item.messageType === MessageType.PROJECT) {
            return route(PROJECTS_ROUTES.detail, { projectId: item.issueId });
        }

        if (item.messageType === MessageType.MACHINE) {
            return {
                pathname: route(MACHINES_DETAILS_ROUTES.machineCommunicationChat, { id: item.machineId || '0' }),
                state: {
                    chatId: item.issueId ? Number(item.issueId) : undefined,
                    title: item.issueDescription || item.intern || '',
                },
            };
        }

        return route(MACHINES_DETAILS_ROUTES.issueDetails, {
            id: item.machineId,
            issueId: item.issueId,
        });

    }, [item.issueId, item.issueDescription, item.intern, item.machineId, item.messageType]);

    const handleClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        if (netWorkStatus === 0 && !item.messageStatus && [MessageType.NOTIFICATION, MessageType.TRAINING, MessageType.TIME_MANAGEMENT].includes(item.messageType)) {
            await updateNotificationsStatusAction({ issueId: item.issueId, type: item.messageType, messageId: Number(item.id) });
            updateUnreadCount();
        }

        history.push(getTargetRoute() as any);
    };

    return (
        <LinkWrapper
            className={`${classes["list-item"]}`}
            to={getTargetRoute}
            onClick={handleClick}
        >
            <div className={classNames(classes["main-wrapper"], unreadClass, importance === 2 ? classes["importance-high"] : '')}>
                <div className={classNames(classes['user-avatar-wrapper'], unreadClass)}>
                    <UserAvatar
                        className={classes["user-avatar"]}
                        avatar={item.senderImage}
                        userName={item.senderName}
                    />
                </div>
                <div className={classes["info"]}>
                    <div className={classes["title"]}>
                        {importance === 2 && <span className={classes["importance-wrapper"]}>
                            <Icon name="warning" size={16} />
                        </span>}
                        <span>{item.messageType === MessageType.PROJECT ? item.issueDescription : item.intern}</span>
                        {item.messageType !== MessageType.PROJECT && item.messageType !== MessageType.MACHINE && <>
                            <span>{item.category}</span>
                            <span>{item.subcategory}</span>
                        </>}
                        {item.messageType === MessageType.MACHINE && item.roleName && (
                            <span>{item.roleName}</span>
                        )}
                    </div>
                    {isChat && (<div className={classes["issue-description"]}>
                        {item.issueDescription}
                    </div>)}
                    <div className={`${classes["message-sent-details"]} ${isChat ? classes["text-overflow"] : ''}`}>
                        {isChat && <span className={classes["sender"]}>{item.senderName}:</span>}
                        <span dangerouslySetInnerHTML={{ __html: cleanHTML }}></span>
                    </div>
                </div>
                <div className={classes["message-sent-date"]}>
                    {messageSentDateTime}
                </div>
            </div>
        </LinkWrapper>
    );
};

export default ListItem;
